import { useEffect } from 'react';

import { Button, Heading, Icon, Modal, Paragraph } from '@zillow/constellation-10';
import { IconWarningFilled } from '@zillow/constellation-icons';

import { PartialMessage } from '@zg-rentals/backend-styled-components';

import {
  emitClickContactSupportEvent,
  emitClickReturnToZillowEvent,
  emitViewBlockedUserModalEvent,
} from './clickstream/ClickstreamEvents';

import type { ValueOf } from 'type-fest';

import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { UserInfoRestrictionsWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { BLOCKED_USER_MODAL_TRIGGER_OBJECTS } from './clickstream/ClickstreamEvents';

type Props = {
  user: ZrmClickstreamUser & { restrictions?: UserInfoRestrictionsWebApiDto };
};

export const BlockedUserModal = ({ user }: Props) => {
  const restrictions = user.restrictions;

  useEffect(() => {
    if (user?.restrictions) {
      emitViewBlockedUserModalEvent({ user });
    }
  }, [user]);

  if (!restrictions) {
    return null;
  }
  const { partialMessages, title, ctaUrl, ctaText } = restrictions;

  const handleClose = () => {
    emitClickReturnToZillowEvent({ user });
  };

  const handleUrlClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string, text: string) => {
    const triggerObject = text.toLowerCase().replace(/\s+/g, '_') as ValueOf<typeof BLOCKED_USER_MODAL_TRIGGER_OBJECTS>;
    emitClickContactSupportEvent({ user, triggerObject });
  };

  return (
    <Modal.Root
      open={true}
      useDismissProps={{ escapeKey: false, outsidePress: false }}
      size={{ base: 'fullScreen', md: 'md' }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content aria-label={title}>
            <Modal.Body>
              <Icon size="lg" css={{ display: 'block', color: 'bg.feedback.warning.hero', marginBottom: '400' }}>
                <IconWarningFilled />
              </Icon>

              <Heading level={1} textStyle="heading-sm" css={{ marginBottom: '400' }}>
                {title}
              </Heading>

              <Paragraph>
                <PartialMessage partialMessages={partialMessages} onUrlClick={handleUrlClick} />
              </Paragraph>
            </Modal.Body>

            <Modal.Footer>
              <Button tone="brand" emphasis="filled" onClick={handleClose} asChild>
                <a href={ctaUrl}>{ctaText}</a>
              </Button>
            </Modal.Footer>

            <Modal.CloseButton>
              <span hidden />
            </Modal.CloseButton>
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default BlockedUserModal;
